import { t } from '@hefe/i18n';
import { ClubCrudApi, ModalsSlice } from '@hefe/store';
import { Modal, Tabs } from 'antd';
import { useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import { CrudConfig, CrudEntity } from '../../models/club-crud.model';
import ClubCrudChildTable from './club-crud-child-table.component';
import ClubCrudForm from './club-crud-form.component';

export interface ClubCrudComponentProps<T extends CrudEntity> {
  /** CRUD resource configuration */
  config: CrudConfig<T>;
}

/**
 * Generic CRUD component that provides full create/read/update/delete functionality.
 *
 * Uses the same CustomTableColumn and FormField types as manual implementations
 * like UsersComponent - no extra abstraction layer.
 *
 * @example
 * ```tsx
 * const config: CrudConfig<Partner> = {
 *   resource: 'partners',
 *   displayName: 'partner',
 *   columns: [
 *     { key: 'name', dataIndex: 'name', title: 'Name', default: true, sortType: 'string' },
 *   ],
 *   formFields: [
 *     { type: 'text', name: 'name', label: 'Name', rules: [{ required: true }] },
 *   ],
 * };
 *
 * <ClubCrudComponent config={config} />
 * ```
 */
export default function ClubCrudComponent<T extends CrudEntity>({
  config,
}: ClubCrudComponentProps<T>) {
  const dispatch = useAppDispatch();

  // RTK Query hooks
  const {
    data: items = [],
    isLoading,
    isFetching,
  } = ClubCrudApi.useFetchAllQuery(config.resource);
  const [deleteItem, { isLoading: isDeleting }] =
    ClubCrudApi.useDeleteMutation();
  const [deleteMultiple, { isLoading: isDeletingMultiple }] =
    ClubCrudApi.useDeleteMultipleMutation();

  // Local state for edit modal
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [isSaving, setIsSaving] = useState(false);

  // Form ID for modal
  const formId = `club-crud-form-${config.resource}`;

  // Handle row click to open edit modal
  const onRowClick = (item: T) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
    setActiveTab('details');
  };

  // Handle delete action
  const handleDelete = async (ids: number[], resetKeys?: () => void) => {
    try {
      if (config.multiDelete && ids.length > 1) {
        await deleteMultiple({ resource: config.resource, ids }).unwrap();
      } else {
        await deleteItem({ resource: config.resource, id: ids[0] }).unwrap();
      }
      resetKeys?.();
    } catch (error) {
      // Error handling is done by RTK Query middleware
    }
  };

  return (
    <div>
      <CustomTable
        rowKey="id"
        name={config.resource}
        columns={config.columns}
        dataSource={items}
        loading={isLoading || isFetching}
        onRowClick={onRowClick}
        addForm={{
          title: t(`${config.resource}.add`),
          formId: `${formId}-create`,
          component: (
            <ClubCrudForm
              config={config}
              formId={`${formId}-create`}
              onSuccess={() => dispatch(ModalsSlice.modalsActions.closeModal())}
            />
          ),
        }}
        deleteAction={{
          action: handleDelete,
          multiDelete: config.multiDelete,
          loading: isDeleting || isDeletingMultiple,
        }}
      />

      {/* Edit Modal */}
      <Modal
        key={selectedItem?.id}
        title={
          config.editTitleKey && selectedItem
            ? String(selectedItem[config.editTitleKey])
            : t(`${config.resource}.edit`)
        }
        open={isModalOpen}
        onOk={() => {
          // Only submit form if on details tab
          if (activeTab === 'details') {
            const form = document.getElementById(
              `${formId}-edit`
            ) as HTMLFormElement;
            form?.requestSubmit();
          }
        }}
        onCancel={handleModalClose}
        confirmLoading={isSaving}
        okButtonProps={{
          style: activeTab !== 'details' ? { display: 'none' } : undefined,
        }}
        zIndex={100}
        width={config.childRelations?.length ? 800 : 520}
      >
        {selectedItem && (
          <>
            {config.childRelations?.length ? (
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'details',
                    label: t('details'),
                    children: (
                      <ClubCrudForm
                        config={config}
                        item={selectedItem}
                        formId={`${formId}-edit`}
                        onSuccess={handleModalClose}
                        onLoadingChange={setIsSaving}
                      />
                    ),
                  },
                  ...config.childRelations.map((childConfig) => ({
                    key: childConfig.relation,
                    label: childConfig.displayName,
                    children: (
                      <ClubCrudChildTable
                        parentResource={config.resource}
                        parentId={selectedItem.id}
                        config={childConfig}
                      />
                    ),
                  })),
                ]}
              />
            ) : (
              <ClubCrudForm
                config={config}
                item={selectedItem}
                formId={`${formId}-edit`}
                onSuccess={handleModalClose}
                onLoadingChange={setIsSaving}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
